import type { Ref } from 'react'

import type { CellState } from '@hazel/shared'

export interface MosaicCellProps {
  readonly cell: number
  readonly columns: number
  readonly state: CellState
  /** Where the keyboard is pointing, if it is pointing here. */
  readonly cursor: boolean
  /** How big to draw the mark, from the layout table. */
  readonly squareClassName: string
  readonly ref?: Ref<HTMLButtonElement>
  readonly onPick: (element: HTMLButtonElement | null) => void
}

/** What each state is called, said aloud as well as shown. */
const SAID: Readonly<Record<CellState, string>> = {
  unknown: 'empty',
  filled: 'filled in',
  marked: 'marked as blank',
}

/**
 * One square.
 *
 * Always a button, so every square is reachable and announced rather than
 * being a silent div — and so a finger works as well as a keyboard. The same
 * rule Stripey's squares follow.
 *
 * **The three states are told apart by shape, not only by colour.** Filled is
 * a solid block, a mark is a cross, and empty is nothing. A nonogram where
 * filled and marked differed only in hue would be unplayable for somebody who
 * cannot tell those hues apart, and the marks are half of how the puzzle is
 * solved rather than decoration. It also survives the television, where a
 * colour read from three metres is a much weaker signal than a shape.
 *
 * Named for what is in it and where it is — "row 2, column 3, marked as blank"
 * — because the cell number is an engine detail and means nothing to anybody
 * looking at a grid.
 */
export function MosaicCell({
  cell,
  columns,
  state,
  cursor,
  squareClassName,
  ref,
  onPick,
}: MosaicCellProps) {
  const row = Math.floor(cell / columns) + 1
  const column = (cell % columns) + 1

  return (
    <button
      type="button"
      ref={ref}
      // One tab stop for the whole grid, not two hundred and twenty-five. Tab
      // lands where the cursor already is.
      tabIndex={cursor ? 0 : -1}
      aria-label={`Row ${row}, column ${column}, ${SAID[state]}`}
      onClick={(event) => {
        onPick(event.currentTarget)
      }}
      style={{ touchAction: 'manipulation' }}
      className={[
        'relative flex aspect-square items-center justify-center overflow-hidden',
        'border-border border p-0 transition-colors',
        state === 'filled' ? 'bg-primary' : 'bg-surface',
        cursor ? 'ring-focus z-10 ring-4' : '',
      ].join(' ')}
    >
      {state === 'marked' ? (
        <span
          aria-hidden="true"
          className={['text-ink-muted font-display leading-none', squareClassName].join(' ')}
        >
          ×
        </span>
      ) : null}
    </button>
  )
}
